import { logger } from '@/utils/logger';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { UserRole } from './useCurrentUserRole';

export const useAddUserMutation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ first_name, last_name, email, mobile_number, time_zone, role, password }: { 
      first_name: string; 
      last_name: string; 
      email: string; 
      mobile_number?: string; 
      time_zone?: string; 
      role: UserRole; 
      password: string; 
    }) => {
      logger.debug('Adding new user via edge function:', email);
      
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          first_name,
          last_name,
          email,
          mobile_number,
          time_zone,
          role,
          password,
          redirect_to: `${window.location.origin}/reset-password`
        }
      });

      if (error) {
        const functionError = error as Error & {
          context?: { json?: () => Promise<{ error?: string }> };
        };

        if (typeof functionError.context?.json === 'function') {
          const details = await functionError.context.json().catch(() => null);
          if (details?.error) {
            throw new Error(details.error);
          }
        }

        throw error;
      }
      if (data?.error) throw new Error(data.error);

      logger.debug('User creation completed successfully:', data);
      return data;
    },
    onSuccess: (data) => {
      logger.debug('User added successfully, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserRole'] });
      queryClient.invalidateQueries({ queryKey: ['adminActivityLogs'] });
      if (data.setupEmailSent) {
        toast({
          title: "User added and setup email sent",
          description: "User has been successfully added to the organization."
        });
        return;
      }

      toast({
        title: "User added, setup email not sent",
        description: data.setupEmailError || "The user was added, but the setup email could not be sent.",
        variant: "destructive"
      });
    },
    onError: (error: any) => {
      logger.error('Error adding user:', error);
      toast({
        title: "Error adding user",
        description: error.message,
        variant: "destructive"
      });
    }
  });
};
